import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Loader2, ListTree, ArrowLeft, Briefcase, Filter, Eye, EyeOff, RotateCcw } from 'lucide-react';
import { confirmDialog } from '../components/ConfirmHost';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const JobKindsPage = () => {
  const { canCreate, canEdit, canDelete } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [jobKinds, setJobKinds] = useState([]);
  const [jobTypes, setJobTypes] = useState([]);
  const [allTypes, setAllTypes] = useState([]);
  const [showPassive, setShowPassive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [selectedTypeFilter, setSelectedTypeFilter] = useState(searchParams.get('type') || 'all');
  const [formData, setFormData] = useState({
    job_type_id: '',
    name: '',
    description: ''
  });

  useEffect(() => {
    loadData();
  }, [showPassive]);

  useEffect(() => {
    // URL parametresini güncelle
    if (selectedTypeFilter && selectedTypeFilter !== 'all') {
      setSearchParams({ type: selectedTypeFilter });
    } else {
      setSearchParams({});
    }
  }, [selectedTypeFilter, setSearchParams]);

  const loadData = async () => {
    try {
      // Pasifler yalnız istendiğinde (düzenleme yetkisiyle) yüklenir
      const [kindsRes, typesRes] = await Promise.all([
        axios.get(`${API}/job-kinds${showPassive ? '/all' : ''}`),
        axios.get(`${API}/job-types${showPassive ? '/all' : ''}`)
      ]);
      setJobKinds(kindsRes.data);
      setAllTypes(typesRes.data);
      setJobTypes(typesRes.data.filter(t => t.is_active !== false));
    } catch (error) {
      toast.error('Veriler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const getJobTypeName = (jobTypeId) => {
    const type = allTypes.find(t => t.id === jobTypeId);
    return type?.name || '-';
  };

  const openDialog = (item = null) => {
    if (item) {
      setEditing(item);
      setFormData({
        job_type_id: item.job_type_id,
        name: item.name,
        description: item.description || ''
      });
    } else {
      setEditing(null);
      setFormData({
        job_type_id: selectedTypeFilter !== 'all' ? selectedTypeFilter : (jobTypes[0]?.id || ''),
        name: '',
        description: ''
      });
    }
    setDialogOpen(true);
  };

  const saveItem = async () => {
    if (!formData.job_type_id) {
      toast.error('İş tipi seçimi zorunludur');
      return;
    }
    if (!formData.name.trim()) {
      toast.error('İş çeşidi adı zorunludur');
      return;
    }

    try {
      if (editing) {
        await axios.put(`${API}/job-kinds/${editing.id}`, formData);
        toast.success('İş çeşidi güncellendi');
      } else {
        await axios.post(`${API}/job-kinds`, formData);
        toast.success('İş çeşidi eklendi');
      }
      setDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'İşlem başarısız');
    }
  };

  const deleteItem = async (item) => {
    if (!(await confirmDialog({ title: 'İş çeşidi silinsin mi?', description: `"${item.name}" iş çeşidini silmek istediğinize emin misiniz?`, confirmLabel: 'Evet, Sil' }))) {
      return;
    }

    try {
      await axios.delete(`${API}/job-kinds/${item.id}`);
      toast.success('İş çeşidi silindi');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Silme işlemi başarısız');
    }
  };

  const activateItem = async (item) => {
    try {
      await axios.put(`${API}/job-kinds/${item.id}/activate`);
      toast.success('İş çeşidi tekrar aktif edildi');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Aktif etme başarısız');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-slate-600" />
      </div>
    );
  }

  const visibleKinds = showPassive ? jobKinds : jobKinds.filter(k => k.is_active);
  const filteredKinds = selectedTypeFilter === 'all' 
    ? visibleKinds 
    : visibleKinds.filter(k => k.job_type_id === selectedTypeFilter);

  // İş tipine göre grupla
  const groupedKinds = filteredKinds.reduce((acc, kind) => {
    const typeName = getJobTypeName(kind.job_type_id);
    if (!acc[typeName]) {
      acc[typeName] = [];
    }
    acc[typeName].push(kind);
    return acc;
  }, {});

  return (
    <div data-testid="job-kinds-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/job-types">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="h-4 w-4 mr-2" />
              İş Tipleri
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">İş Çeşitleri</h1>
            <p className="text-slate-500 mt-1">Her iş tipi için alt çeşit tanımları</p>
          </div>
        </div>
        {canCreate('work_areas') && (
          <Button 
            onClick={() => openDialog()} 
            className="bg-slate-900 hover:bg-slate-800"
            data-testid="add-job-kind-btn"
            disabled={jobTypes.length === 0}
          >
            <Plus className="h-4 w-4 mr-2" />
            İş Çeşidi Ekle
          </Button>
        )}
      </div>

      {/* Uyarı: İş tipi yoksa */}
      {jobTypes.length === 0 && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <Briefcase className="h-8 w-8 text-amber-600" />
              <div>
                <p className="font-medium text-amber-800">Önce İş Tipi Tanımlayın</p>
                <p className="text-sm text-amber-600">
                  İş çeşidi ekleyebilmek için önce en az bir iş tipi tanımlamanız gerekiyor.
                </p>
                <Link to="/job-types">
                  <Button variant="link" className="p-0 h-auto text-amber-700 hover:text-amber-900">
                    İş Tipleri sayfasına git →
                  </Button>
                </Link>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filter */}
      {jobTypes.length > 0 && (
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <Filter className="h-5 w-5 text-slate-400" />
              <Label className="text-slate-600">İş Tipine Göre Filtrele:</Label>
              <Select value={selectedTypeFilter} onValueChange={setSelectedTypeFilter}>
                <SelectTrigger className="w-64" data-testid="job-type-filter">
                  <SelectValue placeholder="İş tipi seçin" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tüm İş Tipleri</SelectItem>
                  {jobTypes.map((type) => (
                    <SelectItem key={type.id} value={type.id}>
                      {type.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Badge variant="outline" className="ml-auto">
                {filteredKinds.length} iş çeşidi
              </Badge>
              {canEdit('work_areas') && (
                <Button variant="outline" size="sm" onClick={() => setShowPassive(v => !v)} data-testid="toggle-passive-job-kinds">
                  {showPassive ? <EyeOff className="h-4 w-4 mr-2" /> : <Eye className="h-4 w-4 mr-2" />}
                  {showPassive ? 'Pasifleri Gizle' : 'Pasifleri Göster'}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Table */}
      {jobTypes.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>İş Çeşitleri Listesi</CardTitle>
            <CardDescription>
              {selectedTypeFilter === 'all' 
                ? 'Tüm iş çeşitlerini görüntüleyin' 
                : `"${getJobTypeName(selectedTypeFilter)}" için tanımlı iş çeşitleri`}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {filteredKinds.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <ListTree className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                <p>Henüz iş çeşidi tanımlanmamış</p>
                <p className="text-sm mt-1">Yeni bir iş çeşidi ekleyerek başlayın</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>İş Çeşidi Adı</TableHead>
                    <TableHead>Bağlı İş Tipi</TableHead>
                    <TableHead>Açıklama</TableHead>
                    <TableHead className="text-right">İşlemler</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredKinds.map((item) => (
                    <TableRow key={item.id} data-testid={`job-kind-row-${item.id}`} className={item.is_active ? '' : 'bg-slate-50 text-slate-400'}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <ListTree className="h-4 w-4 text-slate-400" />
                          {item.name}
                          {!item.is_active && <Badge variant="outline" className="text-slate-500" data-testid={`job-kind-passive-badge-${item.id}`}>Pasif</Badge>}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary" className="font-normal">
                          {getJobTypeName(item.job_type_id)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-slate-500 max-w-xs truncate">
                        {item.description || '-'}
                      </TableCell>
                      <TableCell className="text-right">
                        {!item.is_active ? (
                          <div className="flex justify-end">
                            <Button variant="outline" size="sm" className="text-emerald-700 border-emerald-200 hover:bg-emerald-50" onClick={() => activateItem(item)} data-testid={`activate-job-kind-${item.id}`}>
                              <RotateCcw className="h-4 w-4 mr-1" />Aktif Et
                            </Button>
                          </div>
                        ) : (
                        <div className="flex justify-end gap-1">
                          {canEdit('work_areas') && (
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => openDialog(item)}
                              data-testid={`edit-job-kind-${item.id}`}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                          )}
                          {canDelete('work_areas') && (
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="text-rose-600 hover:text-rose-700"
                              onClick={() => deleteItem(item)}
                              data-testid={`delete-job-kind-${item.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editing ? 'İş Çeşidi Düzenle' : 'Yeni İş Çeşidi Ekle'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="job_type">İş Tipi *</Label>
              <Select 
                value={formData.job_type_id} 
                onValueChange={(value) => setFormData({ ...formData, job_type_id: value })}
              >
                <SelectTrigger data-testid="job-kind-type-select">
                  <SelectValue placeholder="İş tipi seçin" />
                </SelectTrigger>
                <SelectContent>
                  {jobTypes.map((type) => (
                    <SelectItem key={type.id} value={type.id}>
                      {type.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="name">İş Çeşidi Adı *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Örn: Genel, Elektrik, Mekanik"
                data-testid="job-kind-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Açıklama</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="İş çeşidi hakkında açıklama (opsiyonel)"
                rows={3}
                data-testid="job-kind-description-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              İptal
            </Button>
            <Button onClick={saveItem} data-testid="save-job-kind-btn">
              {editing ? 'Güncelle' : 'Ekle'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default JobKindsPage;
